import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import { timeAgo } from './timeAgo';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

interface Invoice {
  id: string;
  merchant_id: string;
  number: string;
  recipient: string;
  amount: number;
  description: string;
  notes: string;
  due_date: string | null;
  status: InvoiceStatus;
  solution: string;
  payment_link: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  solution: 'crypto' | 'stripe' | 'square';
}

const STATUS_STYLES: Record<InvoiceStatus, { className: string; style?: React.CSSProperties }> = {
  draft: {
    className: 'border border-[1px]',
    style: { background: 'var(--dash-hover-bg)', color: 'var(--dash-text-muted)', borderColor: 'var(--dash-border)' },
  },
  sent: {
    className: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  },
  paid: {
    className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  },
  overdue: {
    className: 'bg-red-500/10 text-red-400 border-red-500/30',
  },
  cancelled: {
    className: 'border border-[1px]',
    style: { background: 'var(--dash-hover-bg)', color: 'var(--dash-text-dim)', borderColor: 'var(--dash-border)' },
  },
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

const STATUS_FILTERS: InvoiceStatus[] = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];

export default function Invoices({ solution }: Props) {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [hoveredCancelId, setHoveredCancelId] = useState<string | null>(null);
  const [hoveredLinkId, setHoveredLinkId] = useState<string | null>(null);
  const [hoveredModalCancel, setHoveredModalCancel] = useState(false);
  const [hoveredModalClose, setHoveredModalClose] = useState(false);

  const label = solution === 'stripe' ? 'Stripe Invoices' : solution === 'square' ? 'Square Invoices' : 'Crypto Invoices';

  useEffect(() => {
    loadInvoices();
  }, [user, solution]);

  async function loadInvoices() {
    if (!user) return;
    const { data } = await supabase
      .from('invoices')
      .select('*')
      .eq('solution', solution)
      .order('created_at', { ascending: false });
    if (data) setInvoices(data as Invoice[]);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    let result = invoices;
    if (statusFilter) result = result.filter((inv) => inv.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.number.toLowerCase().includes(q) ||
          inv.recipient.toLowerCase().includes(q) ||
          inv.description.toLowerCase().includes(q),
      );
    }
    return result;
  }, [invoices, search, statusFilter]);

  const overdueCount = invoices.filter(
    (inv) => inv.status === 'sent' && inv.due_date && new Date(inv.due_date) < new Date(),
  ).length;
  const totalOutstanding = invoices
    .filter((inv) => inv.status === 'sent' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + Number(inv.amount), 0);
  const totalPaid = invoices
    .filter((inv) => inv.status === 'paid')
    .reduce((sum, inv) => sum + Number(inv.amount), 0);

  function resetForm() {
    setRecipient('');
    setAmount('');
    setDescription('');
    setDueDate('');
    setNotes('');
  }

  async function handleCreate() {
    if (!recipient || !amount || !user) return;
    setSaving(true);

    const { count } = await supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('merchant_id', user.id);
    const num = (count || 0) + 1001;

    const newInvoice = {
      merchant_id: user.id,
      number: `INV-${num}`,
      recipient,
      amount: parseFloat(amount),
      description,
      notes,
      due_date: dueDate || null,
      status: 'draft',
      solution,
      payment_link: solution === 'crypto' ? `https://pay.bonespay.com/i/${num}` : null,
    };

    const { data, error } = await supabase.from('invoices').insert(newInvoice).select().maybeSingle();
    if (!error && data) {
      setInvoices((prev) => [data as Invoice, ...prev]);
    }
    setSaving(false);
    resetForm();
    setShowModal(false);
  }

  async function updateStatus(id: string, status: InvoiceStatus) {
    const { error } = await supabase.from('invoices').update({ status }).eq('id', id);
    if (!error) {
      setInvoices((prev) => prev.map((inv) => (inv.id === id ? { ...inv, status } : inv)));
    }
  }

  function copyLink(inv: Invoice) {
    if (!inv.payment_link) return;
    navigator.clipboard.writeText(inv.payment_link);
    setCopiedId(inv.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function isOverdue(inv: Invoice) {
    return inv.status === 'sent' && inv.due_date && new Date(inv.due_date) < new Date();
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
    borderColor: 'var(--dash-border)',
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>{label}</h1>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded bg-white text-zinc-900 font-sans text-sm font-medium hover:bg-zinc-200 transition-colors"
        >
          <span className="text-base leading-none">+</span>
          New Invoice
        </button>
      </div>

      {invoices.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="rounded p-4" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
            <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Total Invoices</div>
            <div className="font-mono text-xl" style={{ color: 'var(--dash-text-primary)' }}>{invoices.length}</div>
          </div>
          <div className="rounded p-4" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
            <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Outstanding</div>
            <div className="font-mono text-xl" style={{ color: 'var(--dash-text-primary)' }}>${totalOutstanding.toFixed(2)}</div>
          </div>
          <div className="rounded p-4" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
            <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Collected</div>
            <div className="font-mono text-xl text-emerald-400">${totalPaid.toFixed(2)}</div>
          </div>
          {overdueCount > 0 && (
            <div className="border border-red-500/20 rounded bg-red-500/5 p-4">
              <div className="font-mono text-[10px] text-red-400 uppercase tracking-widest mb-1">Overdue</div>
              <div className="font-mono text-xl text-red-400">{overdueCount}</div>
            </div>
          )}
        </div>
      )}

      {invoices.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>/</span>
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors placeholder:text-zinc-600"
              style={inputStyle}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded px-3 py-2 font-sans text-sm focus:outline-none"
            style={{ background: 'var(--dash-input)', color: 'var(--dash-select-text)', borderColor: 'var(--dash-border)', border: '1px solid var(--dash-border)' }}
          >
            <option value="">All statuses</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading invoices...</p>
      ) : filtered.length === 0 && !search && !statusFilter ? (
        <div className="rounded p-10 text-center" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
          <div className="text-3xl mb-3 opacity-30">&#x229F;</div>
          <p className="font-mono text-sm mb-2" style={{ color: 'var(--dash-text-muted)' }}>No invoices yet</p>
          <p className="font-sans text-sm max-w-md mx-auto leading-relaxed" style={{ color: 'var(--dash-text-faint)' }}>
            {solution === 'stripe'
              ? 'Create invoices to bill your customers via Stripe. Each invoice is emailed directly to the recipient with a secure payment link.'
              : 'Generate crypto payment invoices with shareable payment links. Recipients can pay in supported cryptocurrencies.'}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded p-8 text-center" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>No invoices match your filters.</p>
        </div>
      ) : (
        <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
          <table className="w-full text-sm font-sans">
            <thead>
              <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Invoice</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Recipient</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Due</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Status</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Created</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv, i) => {
                const overdue = isOverdue(inv);
                const isHovered = hoveredRowId === inv.id;
                const rowBg = overdue
                  ? undefined
                  : i % 2 === 0
                    ? 'var(--dash-row-even)'
                    : 'var(--dash-row-odd)';

                return (
                  <tr
                    key={inv.id}
                    className={`transition-colors ${overdue ? 'bg-red-500/5' : ''}`}
                    style={{
                      background: isHovered ? 'var(--dash-hover-bg)' : rowBg,
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={() => setHoveredRowId(inv.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{inv.number}</div>
                      {inv.notes && (
                        <div className="font-sans text-[10px] mt-0.5 truncate max-w-[120px]" style={{ color: 'var(--dash-text-faint)' }} title={inv.notes}>
                          {inv.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-sans text-xs" style={{ color: 'var(--dash-text-muted)' }}>{inv.recipient}</td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>${Number(inv.amount).toFixed(2)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {inv.due_date ? (
                        <span className={overdue ? 'text-red-400' : ''}>
                          {inv.due_date}
                        </span>
                      ) : '--'}
                    </td>
                    <td className="px-4 py-2.5">
                      {(() => {
                        const statusKey = inv.status as InvoiceStatus;
                        const statusDef = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
                        return (
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-mono font-medium border rounded ${statusDef.className}`}
                            style={statusDef.style}
                          >
                            {overdue ? 'Overdue' : STATUS_LABELS[statusKey] || inv.status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {timeAgo(inv.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {inv.status === 'draft' && (
                          <button
                            onClick={() => updateStatus(inv.id, 'sent')}
                            className="font-mono text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Send
                          </button>
                        )}
                        {(inv.status === 'sent' || inv.status === 'overdue') && (
                          <button
                            onClick={() => updateStatus(inv.id, 'paid')}
                            className="font-mono text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                          >
                            Mark Paid
                          </button>
                        )}
                        {inv.status === 'draft' && (
                          <button
                            onClick={() => updateStatus(inv.id, 'cancelled')}
                            className="font-mono text-xs transition-colors"
                            style={{ color: hoveredCancelId === inv.id ? 'var(--dash-text-tertiary)' : 'var(--dash-text-dim)' }}
                            onMouseEnter={() => setHoveredCancelId(inv.id)}
                            onMouseLeave={() => setHoveredCancelId(null)}
                          >
                            Cancel
                          </button>
                        )}
                        {solution === 'crypto' && inv.payment_link && (
                          <button
                            onClick={() => copyLink(inv)}
                            className="font-mono text-xs transition-colors inline-flex items-center gap-1"
                            style={{ color: hoveredLinkId === inv.id ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)' }}
                            onMouseEnter={() => setHoveredLinkId(inv.id)}
                            onMouseLeave={() => setHoveredLinkId(null)}
                          >
                            {copiedId === inv.id ? (
                              <><span className="text-emerald-400">ok</span></>
                            ) : (
                              <>Link</>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="rounded-lg w-full max-w-md mx-4 p-6 shadow-2xl" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-sans text-base font-medium" style={{ color: 'var(--dash-text-primary)' }}>
                {solution === 'stripe' ? 'New Stripe Invoice' : 'New Crypto Invoice'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="transition-colors text-lg leading-none"
                style={{ color: hoveredModalClose ? 'var(--dash-text-tertiary)' : 'var(--dash-text-dim)' }}
                onMouseEnter={() => setHoveredModalClose(true)}
                onMouseLeave={() => setHoveredModalClose(false)}
              >
                x
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>Recipient Email</label>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors placeholder:text-zinc-600"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>Amount (USD)</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 rounded font-mono text-sm focus:outline-none transition-colors placeholder:text-zinc-600"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Services rendered, product order, etc."
                  className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors placeholder:text-zinc-600"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors placeholder:text-zinc-600"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>Notes (internal)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes, not visible to recipient..."
                  rows={2}
                  className="w-full px-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors placeholder:text-zinc-600 resize-none"
                  style={inputStyle}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="px-4 py-2 rounded font-sans text-sm transition-colors"
                style={{
                  color: hoveredModalCancel ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                  borderColor: hoveredModalCancel ? 'var(--dash-border)' : 'var(--dash-border)',
                  border: '1px solid var(--dash-border)',
                }}
                onMouseEnter={() => setHoveredModalCancel(true)}
                onMouseLeave={() => setHoveredModalCancel(false)}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!recipient || !amount || saving}
                className="px-4 py-2 rounded bg-white text-zinc-900 font-sans text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
